import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { financialAPI, projectsAPI } from '@/lib/api-client'
import { formatDocument } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { useIsMobile } from '@/hooks/use-mobile'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Link as LinkIcon,
  X,
  Loader2,
  CheckCircle,
  ChevronRight,
  Search,
  RefreshCw,
  Tag,
} from 'lucide-react'
import { EntityCardList, EntityDetail } from './EntitySummaryView'
import { DetectedEntityCardList, DetectedEntityDetail } from './DetectedEntitiesView'

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('pt-BR')
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  supplier: 'Fornecedor',
  contractor: 'Empreiteiro',
  buyer: 'Comprador',
  investor: 'Investidor',
  purchase: 'Ordem de Compra',
  contract: 'Contrato',
  broker: 'Corretor',
  collaborator: 'Colaborador',
  'co-buyer': 'Co-comprador',
  project_expense: 'Despesa de Obra',
  operational_expense: 'Despesa Operacional',
}

const STATUS_STYLES: Record<string, { bg: string; border: string; badge: string; badgeLabel: string }> = {
  PENDING: {
    bg: '',
    border: '',
    badge: 'border-amber-300 text-amber-700 bg-amber-50',
    badgeLabel: 'Pendente',
  },
  AUTO_MATCHED: {
    bg: 'bg-green-50/50',
    border: 'border-green-200',
    badge: 'border-green-300 text-green-700 bg-green-50',
    badgeLabel: 'Auto',
  },
  MANUAL_MATCHED: {
    bg: 'bg-blue-50/50',
    border: 'border-blue-200',
    badge: 'border-blue-300 text-blue-700 bg-blue-50',
    badgeLabel: 'Manual',
  },
  IGNORED: {
    bg: 'bg-neutral-50',
    border: 'border-neutral-200',
    badge: 'border-neutral-300 text-neutral-500 bg-neutral-50',
    badgeLabel: 'Ignorado',
  },
}

type DetailSelection =
  | { kind: 'none' }
  | { kind: 'transaction'; txId: string }
  | { kind: 'entity'; entityName: string }
  | { kind: 'detected'; document: string }

export function ReconciliationView() {
  const canEdit = usePermission('financial:edit')
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()

  const [selection, setSelection] = useState<DetailSelection>({ kind: 'none' })
  const [leftPanelView, setLeftPanelView] = useState<'transactions' | 'entities' | 'detected'>('transactions')
  const [manualSearch, setManualSearch] = useState('')
  const [manualSearchQuery, setManualSearchQuery] = useState('')
  const [filter, setFilter] = useState('PENDING')
  const [typeFilter, setTypeFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [entityTypeFilter, setEntityTypeFilter] = useState('')
  const [txSearch, setTxSearch] = useState('')
  const [detectedSearch, setDetectedSearch] = useState('')
  const [detectedTypeFilter, setDetectedTypeFilter] = useState('')
  const [catType, setCatType] = useState<'project_expense' | 'operational_expense' | ''>('')
  const [catProjectId, setCatProjectId] = useState('')
  const [catCategoryName, setCatCategoryName] = useState('')

  // Derived for compatibility
  const selectedTxId = selection.kind === 'transaction' ? selection.txId : null
  const selectedEntityName = selection.kind === 'entity' ? selection.entityName : null
  const selectedDetectedDoc = selection.kind === 'detected' ? selection.document : null

  const resetSelection = () => {
    setSelection({ kind: 'none' })
    setCatType('')
    setCatProjectId('')
    setCatCategoryName('')
  }

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['financial'] })
    queryClient.invalidateQueries({ queryKey: ['reconciliation-entities-summary'] })
    queryClient.invalidateQueries({ queryKey: ['reconciliation-detected-entities'] })
  }

  // Queries
  const { data: allTx, isLoading } = useQuery({
    queryKey: ['financial', 'reconciliation', 'pending', { filter, type: typeFilter, dateFrom, dateTo, entityType: entityTypeFilter }],
    queryFn: () => financialAPI.getPendingReconciliation({
      filter: filter || undefined,
      type: typeFilter || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      entityType: entityTypeFilter || undefined,
    }),
  })

  const { data: suggestions, isLoading: loadingSuggestions } = useQuery({
    queryKey: ['financial', 'reconciliation', 'suggestions', selectedTxId],
    queryFn: () => financialAPI.getSuggestions(selectedTxId!),
    enabled: !!selectedTxId,
  })

  const { data: searchResults, isLoading: loadingSearch } = useQuery({
    queryKey: ['financial', 'reconciliation', 'search', manualSearchQuery],
    queryFn: () => financialAPI.searchEntities(manualSearchQuery),
    enabled: manualSearchQuery.length >= 2,
  })

  const { data: entitiesData, isLoading: loadingEntities } = useQuery({
    queryKey: ['reconciliation-entities-summary', { type: typeFilter, dateFrom, dateTo, entityType: entityTypeFilter }],
    queryFn: () => financialAPI.getEntitiesSummary({
      type: typeFilter || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      entityType: entityTypeFilter || undefined,
    }),
  })

  const { data: detectedData, isLoading: loadingDetected } = useQuery({
    queryKey: ['reconciliation-detected-entities'],
    queryFn: () => financialAPI.getDetectedEntities(),
  })

  // Mutations
  const matchMutation = useMutation({
    mutationFn: (data: { transactionId: string; linkedEntityType: string; linkedEntityId: string; linkedEntityName: string }) =>
      financialAPI.matchTransaction(data),
    onSuccess: () => {
      toast.success('Transação vinculada com sucesso!')
      invalidateAll()
      setSelection({ kind: 'none' })
      setManualSearch('')
      setManualSearchQuery('')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const ignoreMutation = useMutation({
    mutationFn: (id: string) => financialAPI.ignoreTransaction(id),
    onSuccess: () => {
      toast.success('Transação ignorada.')
      invalidateAll()
      setSelection({ kind: 'none' })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const unlinkMutation = useMutation({
    mutationFn: (id: string) => financialAPI.unlinkTransaction(id),
    onSuccess: () => {
      toast.success('Transação desvinculada. Agora pode re-conciliar.')
      invalidateAll()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const [pendingUnlinkEntity, setPendingUnlinkEntity] = useState<{ name: string; count: number } | null>(null)
  const [unlinkingEntity, setUnlinkingEntity] = useState<string | null>(null)

  const unlinkAllByEntityMutation = useMutation({
    mutationFn: (entityName: string) => financialAPI.unlinkAllByEntity(entityName),
    onMutate: (entityName) => setUnlinkingEntity(entityName),
    onSuccess: (data: any, entityName) => {
      toast.success(`Agrupamento desfeito — ${data.count} transação(ões) voltaram para Pendente.`)
      invalidateAll()
      if (selectedEntityName === entityName) setSelection({ kind: 'none' })
    },
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => { setUnlinkingEntity(null); setPendingUnlinkEntity(null) },
  })

  const rerunMutation = useMutation({
    mutationFn: () => financialAPI.rerunReconciliation(),
    onSuccess: (data: any) => {
      invalidateAll()
      setSelection({ kind: 'none' })
      if (data.matched > 0) {
        toast.success(data.message)
      } else {
        toast.info('Nenhuma nova conciliação encontrada.')
      }
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const categorizeMutation = useMutation({
    mutationFn: () =>
      financialAPI.updateTransaction(selectedTxId!, {
        category: catCategoryName,
        ...(catType === 'project_expense' && catProjectId ? { projectId: catProjectId } : {}),
      }),
    onSuccess: () => {
      toast.success('Transação categorizada!')
      invalidateAll()
      setCatType('')
      setCatProjectId('')
      setCatCategoryName('')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const { data: catProjectsData } = useQuery({
    queryKey: ['projects', { limit: 100 }],
    queryFn: () => projectsAPI.list({ limit: 100 }),
    enabled: catType === 'project_expense',
  })

  const { data: catExpenseCategories } = useQuery({
    queryKey: ['expense-categories', catType],
    queryFn: () => financialAPI.listFinancialCategories(catType === 'project_expense' ? 'OBRA' : 'OPERACIONAL'),
    enabled: catType === 'project_expense' || catType === 'operational_expense',
  })

  // Derived data
  const allTransactions = (allTx as any[]) || []
  const suggestionList = (suggestions as any[]) || []
  const entityResults = (searchResults as any[]) || []
  const entities = (entitiesData as any[]) || []
  const detectedEntities = (detectedData as any[]) || []

  const filteredDetectedEntities = detectedEntities.filter((e: any) => {
    if (detectedSearch) {
      const q = detectedSearch.toLowerCase()
      const matchName = (e.name || '').toLowerCase().includes(q)
      const matchDoc = (e.document || '').replace(/\D/g, '').includes(q.replace(/\D/g, ''))
      if (!matchName && !matchDoc) return false
    }
    if (detectedTypeFilter === 'INCOME' && !(e.totalIncome > 0)) return false
    if (detectedTypeFilter === 'EXPENSE' && !(e.totalExpense > 0)) return false
    return true
  })

  const searchQuery = txSearch.trim().toLowerCase()

  const transactions = searchQuery
    ? allTransactions.filter((tx: any) => {
        const desc = (tx.rawDescription || tx.description || '').toLowerCase()
        const bank = (tx.bankAccount?.bankName || '').toLowerCase()
        const linked = (tx.linkedEntityName || '').toLowerCase()
        const date = new Date(tx.date).toLocaleDateString('pt-BR')
        const amount = formatCurrency(tx.amount).toLowerCase()
        const amountRaw = String(tx.amount)
        return desc.includes(searchQuery) || bank.includes(searchQuery) || linked.includes(searchQuery) || date.includes(searchQuery) || amount.includes(searchQuery) || amountRaw.includes(searchQuery)
      })
    : allTransactions

  const filteredEntities = searchQuery
    ? entities.filter((e: any) => {
        const name = (e.entityName || '').toLowerCase()
        const types = (e.entityTypes || []).map((t: string) => (ENTITY_TYPE_LABELS[t] || t).toLowerCase()).join(' ')
        return name.includes(searchQuery) || types.includes(searchQuery)
      })
    : entities

  const selectedTx = allTransactions.find((tx: any) => tx.id === selectedTxId)
  const isPending = selectedTx?.reconciliationStatus === 'PENDING'
  const selectedEntity = selectedEntityName
    ? entities.find((e: any) => e.entityName === selectedEntityName)
    : null
  const selectedDetected = selectedDetectedDoc
    ? detectedEntities.find((e: any) => e.document === selectedDetectedDoc)
    : null

  const handleManualSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setManualSearchQuery(manualSearch)
  }

  // ==================== Render helpers ====================

  function renderTransactionDetail() {
    if (!selectedTxId) return null

    if (loadingSuggestions) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {/* Already linked info */}
        {!isPending && selectedTx && (
          <div className={`rounded-lg border p-4 ${
            selectedTx.reconciliationStatus === 'IGNORED'
              ? 'bg-neutral-50 border-neutral-200'
              : 'bg-green-50 border-green-200'
          }`}>
            {selectedTx.reconciliationStatus === 'IGNORED' ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-neutral-600">
                  Esta transação foi <strong>ignorada</strong>.
                </p>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => unlinkMutation.mutate(selectedTx.id)}
                    disabled={unlinkMutation.isPending}
                  >
                    {unlinkMutation.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                    Restaurar
                  </Button>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <p className="text-sm font-medium text-green-800">
                      Vinculada ({selectedTx.reconciliationStatus === 'AUTO_MATCHED' ? 'automático' : 'manual'})
                    </p>
                  </div>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => unlinkMutation.mutate(selectedTx.id)}
                      disabled={unlinkMutation.isPending}
                    >
                      {unlinkMutation.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                      Desvincular
                    </Button>
                  )}
                </div>
                {selectedTx.linkedEntityName && (
                  <p className="text-sm text-green-700 mt-1">
                    {ENTITY_TYPE_LABELS[selectedTx.linkedEntityType] || selectedTx.linkedEntityType}: <strong>{selectedTx.linkedEntityName}</strong>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Auto suggestions (only for PENDING) */}
        {isPending && suggestionList.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide">
              Sugestões Automáticas ({suggestionList.length})
            </h3>
            {suggestionList.map((suggestion: any, index: number) => (
              <Card key={index} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {ENTITY_TYPE_LABELS[suggestion.entityType] || suggestion.entityType}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            suggestion.confidence >= 80
                              ? 'border-green-300 text-green-700 bg-green-50'
                              : suggestion.confidence >= 50
                              ? 'border-amber-300 text-amber-700 bg-amber-50'
                              : 'border-neutral-300 text-neutral-600'
                          }`}
                        >
                          {suggestion.confidence}% confiança
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm font-medium text-neutral-900">{suggestion.entityName}</p>
                      <p className="text-xs text-neutral-500 mt-1">{suggestion.reason}</p>
                    </div>
                    {canEdit && (
                      <Button
                        size="sm"
                        onClick={() =>
                          matchMutation.mutate({
                            transactionId: selectedTxId!,
                            linkedEntityType: suggestion.entityType,
                            linkedEntityId: suggestion.entityId,
                            linkedEntityName: suggestion.entityName,
                          })
                        }
                        disabled={matchMutation.isPending}
                      >
                        <LinkIcon className="mr-1 h-3.5 w-3.5" />
                        Vincular
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {isPending && suggestionList.length === 0 && (
          <div className="rounded-lg border bg-neutral-50 p-4 text-center">
            <p className="text-sm text-neutral-500">Nenhuma sugestão automática encontrada.</p>
            <p className="text-xs text-neutral-400 mt-1">Use a busca manual abaixo para vincular.</p>
          </div>
        )}

        {/* Manual search (only for PENDING) */}
        {isPending && canEdit && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide">
                Busca Manual
              </h3>
              <form onSubmit={handleManualSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <Input
                    placeholder="Buscar por nome ou CNPJ..."
                    value={manualSearch}
                    onChange={(e) => setManualSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button type="submit" variant="secondary" size="sm" disabled={manualSearch.length < 2}>
                  Buscar
                </Button>
              </form>

              {loadingSearch && (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              )}

              {manualSearchQuery.length >= 2 && !loadingSearch && entityResults.length === 0 && (
                <p className="text-sm text-neutral-400 text-center py-2">
                  Nenhum fornecedor ou empreiteiro encontrado para &quot;{manualSearchQuery}&quot;.
                </p>
              )}

              {entityResults.length > 0 && (
                <div className="space-y-2">
                  {entityResults.map((entity: any) => (
                    <Card key={`${entity.entityType}-${entity.entityId}`} className="hover:shadow-sm transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {ENTITY_TYPE_LABELS[entity.entityType] || entity.entityType}
                              </Badge>
                            </div>
                            <p className="mt-1 text-sm font-medium text-neutral-900">{entity.entityName}</p>
                            {entity.document && (
                              <p className="text-xs text-neutral-400">{formatDocument(entity.document)}</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() =>
                              matchMutation.mutate({
                                transactionId: selectedTxId!,
                                linkedEntityType: entity.entityType,
                                linkedEntityId: entity.entityId,
                                linkedEntityName: entity.entityName,
                              })
                            }
                            disabled={matchMutation.isPending}
                          >
                            <LinkIcon className="mr-1 h-3.5 w-3.5" />
                            Vincular
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Categorize as despesa (any non-ignored transaction) */}
        {selectedTx && selectedTx.reconciliationStatus !== 'IGNORED' && canEdit && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide flex items-center gap-2">
                <Tag className="h-3.5 w-3.5" />
                Categorizar Despesa
              </h3>
              {selectedTx.category && !['Receita Importada', 'Despesa Importada'].includes(selectedTx.category) && (
                <p className="text-xs text-neutral-500">
                  Categoria atual: <span className="font-medium text-neutral-700">{selectedTx.category}</span>
                  {selectedTx.project && <span className="text-neutral-400"> · {selectedTx.project.name}</span>}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Select value={catType} onValueChange={(v) => { setCatType(v as any); setCatProjectId(''); setCatCategoryName('') }}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Tipo de despesa..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project_expense">Despesa de Obra</SelectItem>
                    <SelectItem value="operational_expense">Despesa Operacional</SelectItem>
                  </SelectContent>
                </Select>

                {catType === 'project_expense' && (
                  <Select value={catProjectId} onValueChange={setCatProjectId}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Selecione o projeto..." />
                    </SelectTrigger>
                    <SelectContent>
                      {((catProjectsData as any)?.data || []).map((p: any) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {catType && (
                  <Select value={catCategoryName} onValueChange={setCatCategoryName}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Subcategoria..." />
                    </SelectTrigger>
                    <SelectContent>
                      {((catExpenseCategories as any[]) || []).map((c: any) => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Button
                  size="sm"
                  onClick={() => categorizeMutation.mutate()}
                  disabled={
                    !catType ||
                    !catCategoryName ||
                    (catType === 'project_expense' && !catProjectId) ||
                    categorizeMutation.isPending
                  }
                >
                  {categorizeMutation.isPending ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Tag className="mr-1 h-3.5 w-3.5" />
                  )}
                  Categorizar
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Ignore button (only for PENDING) */}
        {isPending && canEdit && (
          <>
            <Separator />
            <Button
              variant="outline"
              className="w-full"
              onClick={() => ignoreMutation.mutate(selectedTxId!)}
              disabled={ignoreMutation.isPending}
            >
              {ignoreMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <X className="mr-2 h-4 w-4" />
              Ignorar esta Transação
            </Button>
          </>
        )}
      </div>
    )
  }

  function renderDetailContent() {
    if (selection.kind === 'none') {
      return (
        <>
          <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide">
            Detalhes
          </h3>
          <div className="flex items-center justify-center rounded-lg border bg-neutral-50 p-16">
            <p className="text-neutral-400">Selecione uma transação ou entidade para ver detalhes.</p>
          </div>
        </>
      )
    }

    if (selection.kind === 'entity' && selectedEntity) {
      return <EntityDetail entity={selectedEntity} />
    }

    if (selection.kind === 'detected' && selectedDetected) {
      return (
        <DetectedEntityDetail
          entity={selectedDetected}
          onClassified={() => setSelection({ kind: 'none' })}
        />
      )
    }

    if (selection.kind === 'transaction') {
      return renderTransactionDetail()
    }

    return null
  }

  // ==================== Main render ====================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Instructions - only for transactions view */}
      {leftPanelView === 'transactions' && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
          <p className="text-sm text-blue-800">
            <strong>Como conciliar:</strong> Selecione uma transação à esquerda. À direita aparecerão sugestões automáticas
            (se houver). Você também pode buscar manualmente por nome ou CNPJ de fornecedor/empreiteiro para vincular.
          </p>
        </div>
      )}

      {/* Shared filters - visible for transactions and entities tabs */}
      {(leftPanelView === 'transactions' || leftPanelView === 'entities') && (
        <div className="flex items-center gap-2 flex-wrap">
          {leftPanelView === 'transactions' && (
            <Select value={filter} onValueChange={(v) => { setFilter(v); resetSelection(); setTxSearch('') }}>
              <SelectTrigger className="w-[150px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pendentes</SelectItem>
                <SelectItem value="MATCHED">Vinculadas</SelectItem>
                <SelectItem value="IGNORED">Ignoradas</SelectItem>
                <SelectItem value="ALL">Todas</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Select value={typeFilter || 'ALL'} onValueChange={(v) => { setTypeFilter(v === 'ALL' ? '' : v); resetSelection() }}>
            <SelectTrigger className="w-[130px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tipo: Todos</SelectItem>
              <SelectItem value="INCOME">Entrada</SelectItem>
              <SelectItem value="EXPENSE">Saída</SelectItem>
            </SelectContent>
          </Select>
          <Select value={entityTypeFilter || 'ALL'} onValueChange={(v) => { setEntityTypeFilter(v === 'ALL' ? '' : v); resetSelection() }}>
            <SelectTrigger className="w-[155px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Entidade: Todas</SelectItem>
              <SelectItem value="supplier">Fornecedor</SelectItem>
              <SelectItem value="buyer">Comprador</SelectItem>
              <SelectItem value="investor">Investidor</SelectItem>
              <SelectItem value="contractor">Empreiteiro</SelectItem>
              <SelectItem value="broker">Corretor</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); resetSelection() }}
            className="w-[140px] h-9"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); resetSelection() }}
            className="w-[140px] h-9"
          />
          {(leftPanelView === 'transactions' || leftPanelView === 'entities') && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <Input
                  placeholder="Buscar..."
                  value={txSearch}
                  onChange={(e) => setTxSearch(e.target.value)}
                  className="pl-10 w-[180px] h-9"
                />
              </div>
              <span className="text-xs text-neutral-400 whitespace-nowrap">
                {leftPanelView === 'transactions'
                  ? `${transactions.length}${allTransactions.length !== transactions.length ? ` de ${allTransactions.length}` : ''} tx`
                  : `${filteredEntities.length}${entities.length !== filteredEntities.length ? ` de ${entities.length}` : ''} entidade(s)`
                }
              </span>
            </>
          )}
          <div className="flex-1" />
          {leftPanelView === 'transactions' && canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => rerunMutation.mutate()}
              disabled={rerunMutation.isPending}
            >
              {rerunMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Re-conciliar
            </Button>
          )}
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_7fr] gap-6">
        {/* Left panel */}
        <div className="space-y-3">
          <Tabs value={leftPanelView} onValueChange={(v) => { setLeftPanelView(v as 'transactions' | 'entities' | 'detected'); resetSelection(); setTxSearch('') }}>
            <TabsList className="h-8">
              <TabsTrigger value="transactions" className="text-xs px-3 py-1.5">
                Transações ({transactions.length})
              </TabsTrigger>
              <TabsTrigger value="entities" className="text-xs px-3 py-1.5">
                Entidades ({filteredEntities.length})
              </TabsTrigger>
              <TabsTrigger value="detected" className="text-xs px-3 py-1.5">
                Detectadas {detectedEntities.length > 0 ? `(${filteredDetectedEntities.length}${filteredDetectedEntities.length !== detectedEntities.length ? ` de ${detectedEntities.length}` : ''})` : ''}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="transactions" className="mt-3">
              {transactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-12">
                  <CheckCircle className="h-12 w-12 text-green-400" />
                  <h3 className="mt-3 text-sm font-medium text-neutral-900">
                    {filter === 'PENDING' ? 'Tudo conciliado!' : 'Nenhuma transação encontrada'}
                  </h3>
                  <p className="mt-1 text-xs text-neutral-500">
                    {filter === 'PENDING'
                      ? 'Não há transações pendentes de conciliação.'
                      : 'Nenhuma transação com esse filtro.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-2">
                  {transactions.map((tx: any) => {
                    const style = STATUS_STYLES[tx.reconciliationStatus] || STATUS_STYLES.PENDING
                    const isLinked = tx.reconciliationStatus === 'AUTO_MATCHED' || tx.reconciliationStatus === 'MANUAL_MATCHED'
                    const isIgnored = tx.reconciliationStatus === 'IGNORED'

                    return (
                      <Card
                        key={tx.id}
                        className={`cursor-pointer transition-all ${
                          selectedTxId === tx.id
                            ? 'ring-2 ring-primary shadow-md'
                            : 'hover:shadow-sm'
                        } ${style.bg} ${style.border ? `border ${style.border}` : ''}`}
                        onClick={() => {
                          setSelection({ kind: 'transaction', txId: tx.id })
                          setManualSearch('')
                          setManualSearchQuery('')
                          setCatType('')
                          setCatProjectId('')
                          setCatCategoryName('')
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              {tx.type === 'INCOME' ? (
                                <ArrowUpCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                              ) : (
                                <ArrowDownCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm font-medium truncate ${isIgnored ? 'text-neutral-400 line-through' : 'text-neutral-900'}`}>
                                  {tx.rawDescription || tx.description}
                                </p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <span className="text-xs text-neutral-500">{formatDate(tx.date)}</span>
                                  {tx.bankAccount && (
                                    <span className="text-xs text-neutral-400">{tx.bankAccount.bankName}</span>
                                  )}
                                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${style.badge}`}>
                                    {style.badgeLabel}
                                  </Badge>
                                </div>
                                {isLinked && tx.linkedEntityName && (
                                  <div className="flex items-center gap-1 mt-1.5">
                                    <LinkIcon className="h-3 w-3 text-green-600" />
                                    <span className="text-xs font-medium text-green-700">{tx.linkedEntityName}</span>
                                    {tx.linkedEntityType && (
                                      <span className="text-[10px] text-neutral-400">
                                        ({ENTITY_TYPE_LABELS[tx.linkedEntityType] || tx.linkedEntityType})
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right ml-3 flex items-center gap-2">
                              <p className={`text-sm font-bold ${tx.type === 'INCOME' ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.type === 'INCOME' ? '+' : '-'}{formatCurrency(tx.amount)}
                              </p>
                              {selectedTxId === tx.id && (
                                <ChevronRight className="h-4 w-4 text-primary" />
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="entities" className="mt-3">
              <EntityCardList
                entities={filteredEntities}
                isLoading={loadingEntities}
                selectedEntityName={selectedEntityName}
                onSelect={(name) => setSelection({ kind: 'entity', entityName: name })}
                onUnlinkAll={(name) => {
                  const entity = filteredEntities.find((e: any) => e.entityName === name)
                  setPendingUnlinkEntity({ name, count: entity?.transactionCount ?? 0 })
                }}
                unlinkingEntity={unlinkingEntity}
              />
            </TabsContent>

            <TabsContent value="detected" className="mt-3 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[160px]">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <Input
                    placeholder="Buscar por nome ou CPF/CNPJ..."
                    value={detectedSearch}
                    onChange={(e) => { setDetectedSearch(e.target.value); resetSelection() }}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                <Select
                  value={detectedTypeFilter}
                  onValueChange={(v) => { setDetectedTypeFilter(v === 'ALL' ? '' : v); resetSelection() }}
                >
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="INCOME">Entradas</SelectItem>
                    <SelectItem value="EXPENSE">Saídas</SelectItem>
                  </SelectContent>
                </Select>
                {(detectedSearch || detectedTypeFilter) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 px-2 text-neutral-500"
                    onClick={() => { setDetectedSearch(''); setDetectedTypeFilter(''); resetSelection() }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <DetectedEntityCardList
                entities={filteredDetectedEntities}
                isLoading={loadingDetected}
                selectedDoc={selectedDetectedDoc}
                onSelect={(doc) => setSelection({ kind: 'detected', document: doc })}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right panel - desktop only */}
        <div className="hidden lg:block space-y-3 max-h-[75vh] overflow-y-auto pr-2">
          {renderDetailContent()}
        </div>
      </div>

      {/* Mobile Sheet drawer */}
      {isMobile && (
        <Sheet
          open={selection.kind !== 'none'}
          onOpenChange={(open) => { if (!open) setSelection({ kind: 'none' }) }}
        >
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>
                {selection.kind === 'transaction'
                  ? (selectedTx?.rawDescription || selectedTx?.description || 'Transação')
                  : selection.kind === 'detected'
                  ? (selectedDetected?.name || 'Entidade Detectada')
                  : selectedEntityName || 'Entidade'}
              </SheetTitle>
              <SheetDescription className="sr-only">
                Detalhes da seleção
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4">
              {selection.kind === 'transaction' && renderTransactionDetail()}
              {selection.kind === 'entity' && selectedEntity && (
                <EntityDetail entity={selectedEntity} />
              )}
              {selection.kind === 'detected' && selectedDetected && (
                <DetectedEntityDetail
                  entity={selectedDetected}
                  onClassified={() => setSelection({ kind: 'none' })}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Confirmação: Desvincular agrupamento */}
      <Dialog open={!!pendingUnlinkEntity} onOpenChange={(open) => { if (!open) setPendingUnlinkEntity(null) }}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Desvincular agrupamento</DialogTitle>
            <p className="text-sm text-neutral-500 mt-1">
              Isso vai desfazer o vínculo de{' '}
              <strong className="text-neutral-700">{pendingUnlinkEntity?.count} transação(ões)</strong> associadas a{' '}
              <strong className="text-neutral-700">{pendingUnlinkEntity?.name}</strong>.
              Elas voltarão para <strong className="text-neutral-700">Pendente</strong> e poderão ser re-conciliadas.
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingUnlinkEntity(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => pendingUnlinkEntity && unlinkAllByEntityMutation.mutate(pendingUnlinkEntity.name)}
              disabled={unlinkAllByEntityMutation.isPending}
            >
              {unlinkAllByEntityMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Desvincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
